/**
 * API Key Service
 *
 * Frontend service for managing API keys for external API access.
 * Communicates with the backend API key routes.
 */

import { apiRequest } from './api';
import type {
  ApiKeyScope,
  ApiKeyListItem,
  RateLimitTier,
  CreateApiKeyRequest,
  UpdateApiKeyRequest
} from '@simcheck/shared-types';

// ============================================================================
// Types
// ============================================================================

export interface ApiKeyCreateResponse {
  apiKey: {
    id: string;
    keyPrefix: string;
    name: string;
    description?: string;
    scopes: ApiKeyScope[];
    rateLimitTier: RateLimitTier;
    status: string;
    createdAt: string;
    expiresAt?: string;
  };
  key: string;  // Full key value - ONLY shown once!
  message: string;
}

export interface ApiKeyListResponse {
  apiKeys: ApiKeyListItem[];
  count: number;
}

export interface ApiKeyRotateResponse {
  apiKey: {
    id: string;
    keyPrefix: string;
    name: string;
    scopes: ApiKeyScope[];
  };
  key: string;  // New key value
  previousKeyPrefix: string;
  message: string;
}

// ============================================================================
// API Key Service Functions
// ============================================================================

/**
 * List all API keys for the current organization
 */
export async function listApiKeys(organizationId: string): Promise<ApiKeyListResponse> {
  return apiRequest<ApiKeyListResponse>('/api-keys', {}, organizationId);
}

/**
 * Get a single API key by ID
 */
export async function getApiKey(id: string, organizationId: string): Promise<ApiKeyListItem> {
  return apiRequest<ApiKeyListItem>(`/api-keys/${id}`, {}, organizationId);
}

/**
 * Create a new API key
 * Returns the full key value - SAVE IT, this is the only time it's shown!
 */
export async function createApiKey(
  request: CreateApiKeyRequest,
  organizationId: string
): Promise<ApiKeyCreateResponse> {
  return apiRequest<ApiKeyCreateResponse>(
    '/api-keys',
    {
      method: 'POST',
      body: JSON.stringify(request),
    },
    organizationId
  );
}

/**
 * Update an API key's settings
 */
export async function updateApiKey(
  id: string,
  request: UpdateApiKeyRequest,
  organizationId: string
): Promise<{ message: string; apiKey: Partial<ApiKeyListItem> }> {
  return apiRequest(
    `/api-keys/${id}`,
    {
      method: 'PUT',
      body: JSON.stringify(request),
    },
    organizationId
  );
}

/**
 * Revoke an API key (soft delete)
 */
export async function revokeApiKey(
  id: string,
  organizationId: string,
  reason?: string
): Promise<{ message: string; keyId: string }> {
  return apiRequest(
    `/api-keys/${id}`,
    {
      method: 'DELETE',
      body: JSON.stringify({ reason }),
    },
    organizationId
  );
}

/**
 * Rotate an API key (generate new secret, keep same ID and settings)
 * Returns the new key value - SAVE IT, this is the only time it's shown!
 */
export async function rotateApiKey(
  id: string,
  organizationId: string
): Promise<ApiKeyRotateResponse> {
  return apiRequest<ApiKeyRotateResponse>(
    `/api-keys/${id}/rotate`,
    {
      method: 'POST',
    },
    organizationId
  );
}

/**
 * Permanently delete an API key (only available for revoked keys)
 */
export async function deleteApiKeyPermanently(
  id: string,
  organizationId: string
): Promise<{ message: string; keyId: string }> {
  return apiRequest(
    `/api-keys/${id}/permanent`,
    {
      method: 'DELETE',
    },
    organizationId
  );
}

// Re-export types for convenience
export type { ApiKeyScope, ApiKeyListItem, RateLimitTier, CreateApiKeyRequest, UpdateApiKeyRequest };
